import React, {useEffect, useRef, useState} from 'react';
import jsQR from 'jsqr';
import ConfirmPopup from './ConfirmPopup.js';

const FormScan = React.forwardRef(({label, value, setValue, error, placeholder = '', maxLength, iconPosTop = '50%'}, ref) => {
	const [showScan, setShowScan] = useState(false);
	const [scanError, setScanError] = useState('');
	const [scanReady, setScanReady] = useState(false);
	const videoRef = useRef(null);
	const canvasRef = useRef(null);
	const foundRef = useRef(false);

	if (placeholder === '') {
		placeholder = label;
	}

	if (!maxLength) {
		maxLength = 255;
	}

	const showMeta = label !== '' || error !== '';
	const iconStyle = iconPosTop === '50%' ? {top: '50%', transform: 'translateY(-50%)'} : {top: iconPosTop};

	const runScan = () => {
		setScanError('');
		setShowScan(true);
	};

	const closeScan = () => {
		setShowScan(false);
		setScanError('');
		setScanReady(false);
	};

	useEffect(() => {
		if (!showScan) {
			foundRef.current = false;
			setScanReady(false);
			return undefined;
		}

		if (!navigator.mediaDevices?.getUserMedia) {
			setScanError('Tento prohlížeč nepodporuje přístup ke kameře.');
			return undefined;
		}

		if (!window.isSecureContext && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
			setScanError('Kamera vyžaduje zabezpečené připojení HTTPS.');
			return undefined;
		}

		let isActive = true;
		let animationFrameId = 0;
		let stream = null;

		const stopStream = () => {
			if (animationFrameId) {
				cancelAnimationFrame(animationFrameId);
			}

			if (stream) {
				stream.getTracks().forEach((track) => track.stop());
				stream = null;
			}

			if (videoRef.current) {
				videoRef.current.srcObject = null;
			}
		};

		const scanFrame = () => {
			if (!isActive || foundRef.current || !videoRef.current || !canvasRef.current) {
				return;
			}

			const video = videoRef.current;
			const canvas = canvasRef.current;
			const context = canvas.getContext('2d', {willReadFrequently: true});

			if (!context || video.readyState !== video.HAVE_ENOUGH_DATA) {
				animationFrameId = requestAnimationFrame(scanFrame);
				return;
			}

			canvas.width = video.videoWidth;
			canvas.height = video.videoHeight;
			context.drawImage(video, 0, 0, canvas.width, canvas.height);
			const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
			const qrCode = jsQR(imageData.data, imageData.width, imageData.height, {
				inversionAttempts: 'dontInvert'
			});

			if (qrCode?.data) {
				foundRef.current = true;
				setValue(qrCode.data);
				setShowScan(false);
				return;
			}

			animationFrameId = requestAnimationFrame(scanFrame);
		};

		const startScan = async () => {
			try {
				stream = await navigator.mediaDevices.getUserMedia({
					audio: false,
					video: {
						facingMode: {
							ideal: 'environment'
						}
					}
				});

				if (!isActive) {
					stopStream();
					return;
				}

				if (videoRef.current) {
					videoRef.current.srcObject = stream;
					await videoRef.current.play();
					setScanReady(true);
					animationFrameId = requestAnimationFrame(scanFrame);
				}
			} catch (err) {
				setScanError('Nepodařilo se spustit fotoaparát. Zkontroluj oprávnění kamery.');
			}
		};

		startScan();

		return () => {
			isActive = false;
			foundRef.current = false;
			stopStream();
		};
	}, [setValue, showScan]);

	return (
		<>
			<div className={`input-div input-div--with-icon${error !== '' ? ' has-error' : ''}`}>
				{showMeta ? (
					<div className="input-meta">
						<div className="input-meta-left">
							{label !== '' ? <div className="input-label">{label}</div> : ''}
						</div>
						{error !== '' ? <div className="input-error">{error}</div> : ''}
					</div>
				) : ''}
				<div className="input-control input-control--with-icon">
					<input className="input-text" type="text" maxLength={maxLength} placeholder={placeholder} ref={ref} value={value} onChange={(e) => setValue(e.target.value)} />
					<img className="input-text-scan-cal-icon" alt="" style={iconStyle} onClick={() => runScan()} />
				</div>
			</div>

			<ConfirmPopup showConfirmPopup={showScan} closeConfirmPopup={closeScan} infoHeader="Naskenovat QR kód" infoMain="">
				<div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem'}}>
					<div style={{width: '100%', maxWidth: '28rem', backgroundColor: '#000000', borderRadius: '0.8rem', overflow: 'hidden'}}>
						<video ref={videoRef} autoPlay playsInline muted style={{width: '100%', display: 'block'}} />
					</div>
					<canvas ref={canvasRef} style={{display: 'none'}} />
					<div>{scanError !== '' ? scanError : scanReady ? 'Namiř fotoaparát na QR kód.' : 'Spouštím kameru...'}</div>
				</div>
			</ConfirmPopup>
		</>
	);
});

export default FormScan;
